import { getImageDimensions } from './adapters';

export interface BrowserImageDimensions {
  width: number;
  height: number;
}

/**
 * Memoized post-EXIF browser dimensions per image URL.
 *
 * MLivePreview's -vert/-horiz template decision needs the image's dimensions AS THE BROWSER
 * RENDERS IT (EXIF rotation applied), which only an actual `<img>` load can provide. The consumer
 * flow uses at most two source images per order pass (one individual + one group), so a tiny
 * in-flight-promise cache means every node/preview that needs the same image shares one load.
 *
 * Failures are NOT memoized — the cache entry is dropped so a transient network error (or an
 * expired SAS token that gets refreshed) can retry on the next call.
 */
const dimensionsByUrl = new Map<string, Promise<BrowserImageDimensions | null>>();

export function resolveBrowserImageDimensions(imageUrl: string): Promise<BrowserImageDimensions | null> {
  if (!imageUrl) return Promise.resolve(null);

  const existing = dimensionsByUrl.get(imageUrl);
  if (existing) return existing;

  const pending = getImageDimensions(imageUrl)
    .then((dims) => (dims.width && dims.height ? { width: dims.width, height: dims.height } : null))
    .catch(() => null)
    .then((dims) => {
      if (!dims) dimensionsByUrl.delete(imageUrl);
      return dims;
    });

  dimensionsByUrl.set(imageUrl, pending);
  return pending;
}
